import { useState } from "react";
import { HeartHandshake, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageTitle, useApi, Skeleton, Stagger, Item, inputCls, btnPrimary, badge } from "@/components/portal/ui";

const TYPES = ["Doa", "Konseling", "Kedukaan", "Kunjungan Kasih", "Ucapan Syukur"];
const TONE = { baru: badge.warn, diproses: badge.info, selesai: badge.success };

export default function Doa() {
  const { data, reload } = useApi("/portal/prayer");
  const [form, setForm] = useState({ type: "Doa", message: "", confidential: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/prayer", form);
      toast.success("Permohonan terkirim ke tim pastoral");
      setForm({ type: "Doa", message: "", confidential: false });
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="portal-doa">
      <PageTitle eyebrow="Tim Pastoral" title="Permohonan Doa" />
      <form onSubmit={submit} className="mt-4 bg-white mx-5 rounded-3xl p-6 shadow-sm border border-slate-100 space-y-4" data-testid="prayer-form">
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`prayer-type-${t.replace(/\s/g, "-")}`} className={`text-xs rounded-full px-3 py-2 transition active:scale-95 ${form.type === t ? "bg-blue-600 text-white font-bold" : "bg-slate-50 border border-slate-200 text-slate-600 font-semibold"}`}>{t}</button>
          ))}
        </div>
        <textarea data-testid="prayer-message-input" rows={5} className={inputCls} placeholder="Tuliskan pokok doa Anda…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
        <label className="flex items-center gap-3 text-sm text-slate-600">
          <input type="checkbox" data-testid="prayer-confidential" checked={form.confidential} onChange={(e) => setForm({ ...form, confidential: e.target.checked })} className="w-4 h-4 accent-blue-600" />
          <Lock className="w-4 h-4" /> Rahasia (hanya untuk gembala)
        </label>
        <button disabled={busy} data-testid="prayer-submit-button" className={`${btnPrimary} w-full`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><HeartHandshake className="w-4 h-4" />Kirim ke Pastoral</>}</button>
      </form>
      <div className="px-5 mt-6">
        <p className="px-1 mb-3 text-sm font-bold text-slate-900">Riwayat Permohonan</p>
        {!data ? <Skeleton className="h-24" /> : (
          <Stagger className="space-y-3">
            {data.length === 0 && <p className="text-sm text-slate-500 px-1">Belum ada permohonan.</p>}
            {data.map((p) => (
              <Item key={p.id} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid={`prayer-${p.id}`}>
                <div className="flex items-center justify-between"><span className="text-[11px] font-bold bg-blue-100 text-blue-700 rounded-full px-3 py-1">{p.type}</span><span className={`text-[11px] font-bold rounded-full px-3 py-1 ${TONE[p.status] || badge.info}`}>{p.status}</span></div>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">{p.message}</p>
                <p className="mt-2 text-xs text-slate-400">{fmtDate(p.created_at, { day: "numeric", month: "short", year: "numeric" })}</p>
              </Item>
            ))}
          </Stagger>
        )}
      </div>
    </div>
  );
}
